/*
//-------------------------------------------------------
//   Copyright (c) 2019 Templa Computer Systems Ltd
//
//   App:        EmployeePortal.UI
//   Object:     Items
//   Details:    Item store
//
// Log      Date   By  Scan Description
// -----  -------- --- ---- ------------------------------
// 102715 16/08/19 JC  M01  Added Contacts
// 103782 10/12/19 JC  M02  Added MyTraining
// WI3713 25/01/24 JC  M03JC    Moved IsExpanded down to item store
//-------------------------------------------------------
*/
import { fetchActions } from "../FetchWrapper";
import { itemCategories } from "../constants/ItemCategories";
import { itemTypes } from '../constants/ItemTypes';
import { userActions } from './Authentication';
import { itemTypeActions } from './ItemTypes';

const requestType = '_ITEMS_REQUEST';
const successType = '_ITEMS_SUCCESS';
const failureType = '_ITEMS_FAILURE';
const storeItemType = '_ITEMS_STOREITEM'; //M02
const completeItemType = '_ITEMS_COMPLETEITEM'; //M02
const pageSize = 20;

const initialState = {
	isLoading: false,
	items: [],
	maxPage: 1,
	page: 1,
	itemTypeId: -1
};

export const itemActions = {
	getPage,
	markAsRead,
    markAsUnread,
    startTraining, //M02
    changeTrainingQuestion, //M02
    answerTrainingQuestion, //M02
    completeTraining,
    updateIsExpanded //M03JC
};

function getPage(page, itemCategory, itemTypeId, expandItemId) { 
    //M03JC added expandItemId
    return (dispatch, getState) => {
		if (!getState().clientConfiguration.config.endpoint)
			return;

		dispatch(itemTypeActions.get(itemCategory));
		dispatch(_request(itemCategory, itemTypeId));

		const requestShape = getRequestShapeForType(getState().clientConfiguration.config.endpoint, itemCategory);

		if (requestShape.error) {
			dispatch(_failure(itemCategory, itemTypeId, requestShape.error));
		} else {

			const ticket = getState().authentication.user.ticket;
			const query = `?page=${encodeURIComponent(page)}`
				+ `&getNew=${encodeURIComponent(requestShape.getNew)}`
				+ `&${requestShape.typeIdParamName}=${encodeURIComponent(itemTypeId)}`
				+ `&pageSize=${pageSize}`;

			const typeNameParam = requestShape.typeNameParam;

			fetchActions.get(requestShape.url + query, ticket)
				.then(
					response => {
						var items = response.data.map((item, i) => {
							// Add a type name property so the UI knows how to display it
							item["itemType"] = typeNameParam;

							// Construct a 'sticky' flag reflecting if item is acknowledged and 
							// we are in an unread list
                            item["isSticky"] = requestShape.getNew && item.isAcknowledged;

                            // M03JC Construct an 'expanded' flag reflecting if item is expanded, defaulting to isAlwaysExpand 
                            //or if the item is the one we need to expand
                            item["isExpanded"] = (item.isAlwaysExpand === true || item.id == expandItemId);

							// Map specific type name property to a standard data property
							if (item.hasOwnProperty(typeNameParam)) {
								item["typeName"] = item[typeNameParam];
								delete item[typeNameParam];
							}
							return item;
						});
						var maxPage = maxPageFromTotalRows(response.headers.get("tep-totalrows"));
						if (page > maxPage) {
							dispatch(getPage(maxPage, itemCategory, itemTypeId));
						}
						else {
							dispatch(_success(itemCategory, itemTypeId, items, page, maxPage));
						}
					},
					error => {
						if (error.sessionExpired) {
							dispatch(userActions.sessionExpired());
						}
						else {
							var message = `Could not get ${requestShape.name}: ${error.message}`;
							dispatch(_failure(itemCategory, itemTypeId, message));
						}
					}
				);
		}
	};

	function maxPageFromTotalRows(rowCount) {
		var rc = parseInt(rowCount, 10) || 0;
		var pages = Math.floor((rc - 1) / pageSize) + 1;
		return pages;
	}

}

function markAsRead(id, itemCategory, itemTypeId) {
	return setAcknowledged(id, itemCategory, true, itemTypeId);
}

function markAsUnread(id, itemCategory, itemTypeId) {
	return setAcknowledged(id, itemCategory, false, itemTypeId);
}

function setAcknowledged(id, itemCategory, state, itemTypeId) {
	return (dispatch, getState) => {
		dispatch(_request(itemCategory, itemTypeId));

		const requestShape = getRequestShapeForType(getState().clientConfiguration.config.endpoint, itemCategory);

		if (requestShape.error) {
			dispatch(_failure(itemCategory, itemTypeId, requestShape.error));
		} else {

			const ticket = getState().authentication.user.ticket;
			const action = state ? "read" : "notread";
			const urlComponents = `/${action}/${encodeURIComponent(id)}`;

			fetchActions.post(requestShape.url + urlComponents, ticket)
				.then(
					() => {
						setTimeout(function () {
                            const page = getStoreForCategory(getState, itemCategory).page; //M02 was getPageForCategory
							dispatch(getPage(page, itemCategory, itemTypeId));
						}, 500);
					},
					error => {
						if (error.sessionExpired) {
							dispatch(userActions.sessionExpired());
						}
						else {
							var message = `Could not set read/unread for ${requestShape.name}: ${error.message}`;
							dispatch(_failure(itemCategory, itemTypeId, message));
						}
					}
				);
		}
	};

}

function startTraining(id, itemCategory, items) {
    return (dispatch) => {

        //create new items array
        var newItemsArray = [];
               
        //go through each current item
        items.forEach(function (item, itemindex) {

            //if the item matches the id
            if (item && item.id === id) {

                //clone the item
                var itemToChange = { ...item };

                //put relevant properties onto the item
                itemToChange.isAnsweringTraining = true; //flag as answering training
                itemToChange.currentQuestionIndex = 0; //starts on the first question
                itemToChange.employeeAnswers = [-1]; //employee first answer is -1

                // and add the item for the new array
                newItemsArray.push(itemToChange);

            } else {

                // otherwise clone the item for the new array
                newItemsArray.push({ ...item });
            }
        });              
        
        //and dispatch to store
        dispatch(_storeItem(itemCategory, newItemsArray));
    };
}

function changeTrainingQuestion(id, itemCategory, items, forward) {
    return (dispatch) => {

        //create new items array
        var newItemsArray = [];

        //go through each current item
        items.forEach(function (item) {

            //if the item matches the id
            if (item && item.id === id) {

                //clone the item
                var itemToChange = { ...item };

                //push the question index forward/back
                if (forward) {
                    itemToChange.currentQuestionIndex = itemToChange.currentQuestionIndex + 1;

                    //if there's no employee answer for this index then add
                    if (itemToChange.employeeAnswers.length === itemToChange.currentQuestionIndex) {

                        //create new employee answers array
                        var newEmployeeAnswers = [];

                        //go through each item answer
                        item.employeeAnswers.forEach(function (employeeAnswer) {

                            // add the employee answer to the new array
                            newEmployeeAnswers.push(employeeAnswer);
                        });

                        //add the new answer to the employee answers
                        newEmployeeAnswers.push(-1);

                        //store the new employee answers on the item to change
                        itemToChange.employeeAnswers = newEmployeeAnswers;
                    }

                } else {
                    //push back
                    itemToChange.currentQuestionIndex = itemToChange.currentQuestionIndex - 1;
                }
                          
                // and add the item for the new array
                newItemsArray.push(itemToChange);

            } else {

                // otherwise clone the item for the new array
                newItemsArray.push({ ...item });
            }
        });

        //and dispatch to store
        dispatch(_storeItem(itemCategory, newItemsArray));
    };

}

function answerTrainingQuestion(id, itemCategory, items, answerIndex) {
    return (dispatch) => {

        //create new items array
        var newItemsArray = [];

        //go through each current item
        items.forEach(function (item) {

            //if the item matches the id
            if (item && item.id === id) {

                //clone the item
                var itemToChange = { ...item };

                //create new employee answers array
                var newEmployeeAnswers = [];

                //go through each item answer
                item.employeeAnswers.forEach(function (employeeAnswer, employeeAnswerIndex) {

                    //if the index matches the current question index
                    if (employeeAnswerIndex === itemToChange.currentQuestionIndex) {

                        // and add the answer to the new array
                        newEmployeeAnswers.push(answerIndex);

                    } else {
                        // otherwise add the employee answer to the new array
                        newEmployeeAnswers.push(employeeAnswer);
                    }
                });

                //store the new employee answers on the item to change
                itemToChange.employeeAnswers = newEmployeeAnswers;

                // and add the item for the new array
                newItemsArray.push(itemToChange);

            } else {

                // otherwise clone the item for the new array
                newItemsArray.push({ ...item });
            }
        });

        //and dispatch to store
        dispatch(_storeItem(itemCategory, newItemsArray));
    };

}

function completeTraining(id, itemCategory, itemTypeId, items) {
    return (dispatch, getState) => {
        if (!getState().clientConfiguration.config.endpoint)
            return;

        dispatch(_completing());

        //get the request shape & type name param
        const requestShape = getRequestShapeForType(getState().clientConfiguration.config.endpoint, itemCategory);
        const typeNameParam = requestShape.typeNameParam;

        //set URL and auth ticket
        const url = getState().clientConfiguration.config.endpoint + "/employeetrainings/complete";
        const urlComponents = `/${encodeURIComponent(id)}`;
        const ticket = getState().authentication.user.ticket;
        var data = buildReturnData(id, items);

        //post to the API
        fetchActions.post(url + urlComponents, ticket, data)
            .then(
                response => {
                    //no data? - this should never happen though
                    if (!response.data) {
                        dispatch(_failure(itemCategory, itemTypeId, "Could not complete training"));
                       }
                    setTimeout(function () {
                        
                        //create new items array
                        var newItemsArray = [];

                        //go through each current item
                        items.forEach(function (item) {

                            //if the item matches the id
                            if (item && item.id === id) {

                                //get the data as a new item
                                var newItem = response.data;

                                // Add a type name property so the UI knows how to display it
                                newItem["itemType"] = typeNameParam;

                                // M03JC copy over the isexpanded property
                                newItem["isExpanded"] = item.hasOwnProperty("isExpanded") && item.isExpanded;

                                //push the item into the new items array
                                newItemsArray.push(newItem);

                            } else {

                                // otherwise clone the item for the new array
                                newItemsArray.push({ ...item });
                            }
                        });

                        //and dispatch to store
                        dispatch(_storeItem(itemCategory, newItemsArray));

                    }, 500);
                },
                error => {
                    if (error.sessionExpired) {
                        dispatch(userActions.sessionExpired());
                    }
                    else {
                        var message = `Could not save detail: ${error.message}`;
                        dispatch(_failure(message));
                    }
                }
            );
    };

    function buildReturnData(id, items) {

        //data to return
        var returnData = {};

        //go through each current item
        items.forEach(function (item) {

            //if the item matches the id
            if (item && item.id === id) {

                //go through each question on the item
                item.questions.forEach(function (question, questionIndex) {

                    //the return data for the question's line number is the line number of the answer relating to the answer index for the question index
                    returnData[question.lineNumber] = question.answers[item.employeeAnswers[questionIndex]].lineNumber;

                }); //next question
            }


        }); //next item

        //return
        return returnData;
    }

    function _completing() { return { type: completeItemType }; }
}

function updateIsExpanded(id, itemCategory, items) {
    return (dispatch, getState) => {
        if (!getState().clientConfiguration.config.endpoint)
            return;

        //get the request shape & type name param
        const requestShape = getRequestShapeForType(getState().clientConfiguration.config.endpoint, itemCategory);
        const typeNameParam = requestShape.typeNameParam;

        //create new items array
        var newItemsArray = [];

        //go through each current item
        items.forEach(function (item) {

            //copy over the item is expanded property
            let newIsExpanded = item.isExpanded;

            //if the id matches
            if (item && item.id === id) {
                //change the is expanded value
                newIsExpanded = !newIsExpanded;
            }            

            // clone the item for the new array, setting isExpanded
            newItemsArray.push({ ...item, isExpanded: newIsExpanded });

        });

        //and dispatch to store
        dispatch(_storeItem(itemCategory, newItemsArray));
    };
}

// Gets information on how the request should appear for the recognised types
function getRequestShapeForType(baseUrl, itemCategory) {
	switch (itemCategory) {
		case itemCategories.unreadNotices:
			return {
				name: "unread notices",
				url: baseUrl + "/employeenotices",
				typeIdParamName: "NoticeTypeId",
				typeNameParam: itemTypes.notice,
				getNew: true
			};

		case itemCategories.readNotices:
			return {
				name: "read notices",
				url: baseUrl + "/employeenotices",
				typeIdParamName: "NoticeTypeId",
				typeNameParam: itemTypes.notice,
				getNew: false
			};

		case itemCategories.unreadDocuments:
			return {
				name: "unread documents",
				url: baseUrl + "/employeedocuments",
				typeIdParamName: "DocumentTypeId",
				typeNameParam: itemTypes.document,
				getNew: true
			};

		case itemCategories.readDocuments:
			return {
				name: "read documents",
				url: baseUrl + "/employeedocuments",
				typeIdParamName: "DocumentTypeId",
				typeNameParam: itemTypes.document,
				getNew: false
			};

		case itemCategories.unreadPay:
			return {
				name: "unread pay",
				url: baseUrl + "/payrecords",
				typeIdParamName: "PayRecordTypeId",
				typeNameParam: itemTypes.payRecord,
				getNew: true
			};

		case itemCategories.readPay:
			return {
				name: "read pay",
				url: baseUrl + "/payrecords",
				typeIdParamName: "PayRecordTypeId",
				typeNameParam: itemTypes.payRecord,
				getNew: false
			};

		case itemCategories.unreadRequests:
			return {
				name: "unread requests",
				url: baseUrl + "/requests",
				typeIdParamName: "RequestTypeId",
				typeNameParam: itemTypes.request,
				getNew: true
			};

		case itemCategories.readRequests:
			return {
				name: "read requests",
				url: baseUrl + "/requests",
				typeIdParamName: "RequestTypeId",
				typeNameParam: itemTypes.request,
				getNew: false
            };

        //M01 handle contacts
        case itemCategories.allContacts:
            return {
                name: "contacts",
                url: baseUrl + "/contacts",
                typeNameParam: itemTypes.contact,
            };

        case itemCategories.uncompletedTraining: //M02
            return {
                name: "uncompleted training",
                url: baseUrl + "/employeetrainings",
                typeIdParamName: "TrainingTypeId",
                typeNameParam: itemTypes.training,
                getNew: true
            };

        case itemCategories.completedTraining: //M02
            return {
                name: "completed training",
                url: baseUrl + "/employeetrainings",
                typeIdParamName: "TrainingTypeId",
                typeNameParam: itemTypes.training,
                getNew: false
            };

		default:
			return {
				error: "Unrecognised item category in Items.js:getRequestShapeForType: '" + itemCategory + "'."
			};
	}
}

function _request(itemCategory, itemTypeId) { return { type: itemCategory + requestType, itemTypeId: itemTypeId }; }
function _success(itemCategory, itemTypeId, items, page, maxPage) {
	return { type: itemCategory + successType, itemTypeId: itemTypeId, items, page, maxPage };
}
function _failure(itemCategory, itemTypeId, error) { return { type: itemCategory + failureType, itemTypeId: itemTypeId, error }; }
function _storeItem(itemCategory, items) { return { type: itemCategory + storeItemType, items }; }


//M02 
function getStoreForCategory(getState, itemCategory) {
    switch (itemCategory) {
        case itemCategories.unreadNotices:
            return getState().unreadNotices;
        case itemCategories.readNotices:
            return getState().readNotices;
        case itemCategories.unreadDocuments:
            return getState().unreadDocuments;
        case itemCategories.readDocuments:
            return getState().readDocuments;
        case itemCategories.unreadPay:
            return getState().unreadPay;
        case itemCategories.readPay:
            return getState().readPay;
        case itemCategories.unreadRequests:
            return getState().unreadRequests;
        case itemCategories.readRequests:
            return getState().readRequests;
        case itemCategories.uncompletedTraining: 
            return getState().uncompletedTraining;
        case itemCategories.completedTraining:
            return getState().completedTraining;
        default:
            //return empty object with a page property
            return {page: 0};
    }
}

export const createNamedReducer = (itemCategory = '') => {
	return (state, action) => {
		state = state || initialState;

		switch (action.type) {
			case itemCategory + requestType:
				return { ...state, isLoading: true, itemTypeId: action.itemTypeId };

			case itemCategory + successType:
				return { ...state, isLoading: false, itemTypeId: action.itemTypeId, items: action.items, page: action.page, maxPage: action.maxPage, error: undefined };

			case itemCategory + failureType:
                return { ...state, isLoading: false, itemTypeId: action.itemTypeId, error: action.error };

            //M02 store single item
            case itemCategory + storeItemType:
                return { ...state, items: action.items };

            //M02 complete single item
            case itemCategory + completeItemType:
                return { ...state, items: action.items };

			default:
				return state;
		}
	};
};